/**
 * Stepper — the reusable horizontal stage-stepper primitive (E10-F02-T07).
 *
 * The legacy project card renders the staffing pipeline as a row of numbered
 * circles on a connecting track (Pre-Kickoff → Kick-off → Generation →
 * Launched). The legacy front-end (`public/assets/js/proj*.js → updateStepper`)
 * always draws exactly FOUR steps — the DB carries a fifth stage (5 = the
 * post-launch "Launched" state) which COLLAPSES into step 4 (`mapStage:
 * s === 5 ? 4 : s`). This primitive is generic over the step list, so the
 * collapse lives in the consumer (E02-F02-T04): it passes the 4 collapsed steps
 * and the already-mapped `current` index.
 *
 * Visual contract (status item acceptance + design.md tokens — `#0f172a` =
 * `bg-primary` = slate-900 navy, `#d4af37` = `border-accent` = gold):
 *   - the CURRENT step (`.step-active`)  — gold ring/border, white fill, navy
 *     number (the highlighted "you are here" marker);
 *   - COMPLETED steps (`.step-completed`) — navy fill, white number;
 *   - FUTURE steps — a plain gray-bordered hollow circle;
 *   - a PREVIEW step (`.step-preview`) — a blue ring/border overlay used while
 *     the user is *viewing* a non-current stage (legacy `setProjectStageView`:
 *     previewing a stage WITHOUT changing `project.stage`). The preview marker
 *     wins the styling over active/completed/future so it is unambiguous which
 *     stage is on screen.
 *
 * Behaviour:
 *   - every step is a real `<button>` so it is keyboard-focusable and
 *     screen-reader announced; clicking a step calls `onStepClick(index)` so the
 *     consumer can switch the displayed stage content (preview) — clicking does
 *     NOT mutate the real stage, that is the consumer's concern.
 *   - the whole control is `role="tablist"`-flavoured via `aria-current` on the
 *     active step and `aria-label` per step.
 *
 * It is presentation-only: no stage machine, no preview banner (that is the
 * project-card feature, E02-F02-T04). Colours go through the brand token
 * utilities mapped in styles/index.css @theme — never literal hexes
 * (design.md "Do's and Don'ts").
 */
import type { ReactNode } from 'react';

/** A single step descriptor. */
export interface StepperStep {
  /** Visible label under (or beside) the circle. */
  readonly label: ReactNode;
  /**
   * The number shown inside the circle. Defaults to the 1-based position when
   * omitted — pass it explicitly only if the displayed number differs from the
   * position (e.g. a collapsed stage that should still read "5").
   */
  readonly number?: number;
  /** Disable interaction for this step (still rendered, not clickable). */
  readonly disabled?: boolean;
}

export interface StepperProps {
  /** Ordered steps, left → right. */
  readonly steps: readonly StepperStep[];
  /**
   * Zero-based index of the project's CURRENT step. Steps before it render as
   * completed, the step itself as active, steps after it as future. Clamp /
   * collapse (e.g. stage 5 → 4) in the consumer before passing it.
   */
  readonly current: number;
  /**
   * Zero-based index of the step being PREVIEWED, if any. When set (and it is
   * not the current step) that step gets the blue preview ring — the legacy
   * `setProjectStageView` "Viewing stage X" mode. Null/undefined = no preview.
   */
  readonly preview?: number | null;
  /**
   * Step-click handler — fired with the clicked step's zero-based index. The
   * consumer uses it to switch the displayed stage content (preview), without
   * changing the real `project.stage`.
   */
  readonly onStepClick?: (index: number) => void;
  /** Accessible label for the whole stepper. Default "Project stages". */
  readonly ariaLabel?: string;
  /** Test id prefix; each step button gets `<testId>-step-<index>`. */
  readonly testId?: string;
  /** Extra classes appended to the stepper root. */
  readonly className?: string;
}

/** The three intrinsic states a step can be in relative to `current`. */
type StepState = 'completed' | 'active' | 'future';

function stateFor(index: number, current: number): StepState {
  if (index < current) return 'completed';
  if (index === current) return 'active';
  return 'future';
}

/**
 * Circle classes per state. Preview wins over the intrinsic state (it is layered
 * on top in {@link circleClass}). Kept as exported constants so tests (and the
 * design system) can assert the exact class contract from the status item:
 *   active    → `.step-active`    gold border, white fill, navy number
 *   completed → `.step-completed` navy fill, white number
 *   future    → gray border, hollow
 *   preview   → blue border + ring
 */
export const STEP_CIRCLE_BASE =
  'flex h-9 w-9 shrink-0 items-center justify-center rounded-full border-2 text-sm font-semibold transition-colors';

export const STEP_STATE_CLASS: Record<StepState, string> = {
  active: 'step-active border-accent bg-white text-primary',
  completed: 'step-completed border-primary bg-primary text-white',
  // Legacy `updateStepper` future step: `border-gray-300 text-gray-300 bg-white`
  // (the un-reached number is the light gray-300, not the darker muted token).
  future: 'border-gray-300 bg-white text-gray-300',
};

/** Blue preview overlay — applied on top of the intrinsic state class. */
export const STEP_PREVIEW_CLASS = 'step-preview border-info ring-2 ring-info/40';

function circleClass(state: StepState, isPreview: boolean): string {
  const base = `${STEP_CIRCLE_BASE} ${STEP_STATE_CLASS[state]}`;
  return isPreview ? `${base} ${STEP_PREVIEW_CLASS}` : base;
}

export function Stepper({
  steps,
  current,
  preview = null,
  onStepClick,
  ariaLabel = 'Project stages',
  testId = 'stepper',
  className,
}: StepperProps): React.JSX.Element {
  return (
    <ol
      data-testid={testId}
      aria-label={ariaLabel}
      className={`flex w-full items-start ${className ?? ''}`}
    >
      {steps.map((step, index) => {
        const state = stateFor(index, current);
        const isPreview = preview !== null && preview === index && index !== current;
        const isLast = index === steps.length - 1;
        const number = step.number ?? index + 1;
        const clickable = !step.disabled && onStepClick !== undefined;

        return (
          <li
            key={index}
            data-testid={`${testId}-item-${index}`}
            className={`flex min-w-0 flex-1 flex-col items-center ${isLast ? 'flex-none' : ''}`}
          >
            <div className="flex w-full items-center">
              {/* Left half of the connecting track (hidden on the first step). */}
              <span
                aria-hidden
                className={`h-0.5 flex-1 ${index === 0 ? 'invisible' : index <= current ? 'bg-primary' : 'bg-gray-300'}`}
              />
              <button
                type="button"
                data-testid={`${testId}-step-${index}`}
                aria-label={typeof step.label === 'string' ? step.label : `Stage ${number}`}
                aria-current={index === current ? 'step' : undefined}
                data-state={state}
                data-preview={isPreview ? 'true' : undefined}
                disabled={step.disabled}
                onClick={clickable ? () => onStepClick?.(index) : undefined}
                className={`${circleClass(state, isPreview)} ${clickable ? 'cursor-pointer hover:opacity-90 focus:outline-none focus-visible:ring-2 focus-visible:ring-accent/50' : 'cursor-default'} disabled:cursor-not-allowed disabled:opacity-60`}
              >
                {state === 'completed' && !isPreview ? (
                  <i className="fa-solid fa-check" aria-hidden />
                ) : (
                  number
                )}
              </button>
              {/* Right half of the connecting track (hidden on the last step). */}
              <span
                aria-hidden
                className={`h-0.5 flex-1 ${isLast ? 'invisible' : index < current ? 'bg-primary' : 'bg-gray-300'}`}
              />
            </div>
            {/* Legacy stepper label markup (index.html): every label is
                `text-xs font-bold uppercase tracking-wider` (rendered UPPERCASE
                via CSS — the raw string stays title-case for the aria-label /
                preview banner). The current step keeps the stronger text token. */}
            <span
              data-testid={`${testId}-label-${index}`}
              className={`mt-2 max-w-[8rem] truncate text-center text-xs font-bold uppercase tracking-wider ${index === current ? 'text-text-strong' : 'text-text-muted'}`}
            >
              {step.label}
            </span>
          </li>
        );
      })}
    </ol>
  );
}
